import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { Product } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import OrderFormModal from '../components/OrderFormModal';
import { categoryLabel } from '../utils/productCategories';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

const AllDeliveriesPage: React.FC = () => {
  useDocumentTitle('Јавне испоруке — Манастир Градац', '/isporuke');
  const { t } = useScript();
  const langFade = useLangFade();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderProduct, setOrderProduct] = useState<Product | null>(null);

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getProducts()
      .then(items => { setProducts(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || 'Грешка при учитавању.');
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm(t('Обрисати овај производ?'))) return;
    try {
      await api.deleteProduct(token, id);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  const moveProduct = async (index: number, direction: 'up' | 'down') => {
    if (!token) return;
    const other = direction === 'up' ? index - 1 : index + 1;
    const updated = [...products];
    const tempOrder = updated[index].sortOrder;
    updated[index] = { ...updated[index], sortOrder: updated[other].sortOrder };
    updated[other] = { ...updated[other], sortOrder: tempOrder };
    [updated[index], updated[other]] = [updated[other], updated[index]];
    setProducts(updated);
    try {
      await api.reorderProducts(token, [
        { id: updated[index].id, sortOrder: updated[index].sortOrder },
        { id: updated[other].id, sortOrder: updated[other].sortOrder },
      ]);
    } catch {
      loadProducts();
    }
  };

  return (
    <div className="news-page">
      <div className="news-page-header">
        <h1 className="news-heading">{t('ЈАВНЕ ИСПОРУКЕ')}</h1>
        <p className="deliveries-intro">
          {t('Производи које манастирско братство ручно прави — мед, мелеми, балзами, уља и сапуни. Поручите остављањем контакт података, без плаћања картицом.')}
        </p>
        {isAdmin && (
          <div className="news-admin-toolbar">
            <button className="btn-primary" onClick={() => navigate('/isporuke/nova')}>
              {t('+ Додај производ')}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/narudzbine')}>
              {t('Наруџбине')}
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="state-block state-error" role="alert">
          <span>{t(error)}</span>
          <button type="button" className="state-retry" onClick={loadProducts}>
            {t('Покушај поново')}
          </button>
        </div>
      )}

      {!loading && !error && products.length === 0 && (
        <div className="state-block"><span>{t('Тренутно нема производа.')}</span></div>
      )}

      {!loading && !error && products.length > 0 && (
        <div className={`news-grid news-grid-full ${langFade}`}>
          {products.map((p, index) => (
            <article className={`news-card product-card ${!p.inStock ? 'product-sold-out' : ''}`} key={p.id}>
              <div className="news-image">
                <img
                  src={p.imageUrl}
                  alt={t(p.name)}
                  onError={handleImgError}
                  width={800}
                  height={500}
                  decoding="async"
                  loading="lazy"
                />
                {p.category && <div className="product-category-badge">{t(categoryLabel(p.category))}</div>}
                {!p.inStock && <div className="product-soldout-label">{t('Распродато')}</div>}
                {isAdmin && (
                  <div className="news-admin-overlay news-admin-overlay-visible">
                    <button className="news-admin-edit" disabled={index === 0} onClick={() => moveProduct(index, 'up')} title={t('Помери горе')} aria-label={t('Помери горе')}>↑</button>
                    <button className="news-admin-edit" disabled={index === products.length - 1} onClick={() => moveProduct(index, 'down')} title={t('Помери доле')} aria-label={t('Помери доле')}>↓</button>
                    <button className="news-admin-edit" onClick={() => navigate(`/isporuke/${p.id}/uredi`)} title={t('Уреди')} aria-label={t('Уреди')}>✎</button>
                    <button className="news-admin-delete" onClick={() => handleDelete(p.id)} title={t('Обриши')} aria-label={t('Обриши')}>✕</button>
                  </div>
                )}
              </div>
              <div className="news-body">
                <h3 className="news-title">{t(p.name)}</h3>
                <p className="news-excerpt">{t(p.description)}</p>
                <div className="product-meta">
                  {p.price && <span className="product-price">{t(p.price)}</span>}
                  {p.unit && <span className="product-unit">{t(p.unit)}</span>}
                </div>
                {p.inStock ? (
                  <button className="btn-primary product-order-btn" onClick={() => setOrderProduct(p)}>
                    {t('Наручи')}
                  </button>
                ) : (
                  <span className="product-soldout-note">{t('Тренутно недоступно')}</span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {orderProduct && (
        <OrderFormModal product={orderProduct} onClose={() => setOrderProduct(null)} />
      )}
    </div>
  );
};

export default AllDeliveriesPage;
